/**
 * @file `useTrack` — the call-site API for product analytics.
 *
 * Usage:
 *   const trackEvent = useTrack();
 *   trackEvent(ANALYTICS_EVENTS.REPORT_CREATED, { report_type: 'html' });
 *
 * The event name constrains its own property payload, so a typo in either the
 * name or the properties is a compile error.
 */
import { useCallback } from 'react';
import { usePostHog } from 'posthog-js/react';
import { setPersonProperty, track } from './analytics';
import type { AnalyticsEventName, AnalyticsEventProperties } from './events';

export type TrackFn = <E extends AnalyticsEventName>(
  event: E,
  properties: AnalyticsEventProperties[E],
) => void;

export function useTrack(): TrackFn {
  const posthog = usePostHog();

  return useCallback<TrackFn>(
    (event, properties) => track(posthog, event, properties),
    [posthog],
  );
}

export type SetPersonPropertyFn = (
  properties: Record<string, string | number | boolean>,
) => void;

/**
 * Records a *setting's current value* on the person, for questions about state
 * rather than activity ("how many people have Slack notifications on"). Pair it
 * with the event that changed the setting: the event gives you the trend, the
 * property gives you the standing answer.
 */
export function useSetPersonProperty(): SetPersonPropertyFn {
  const posthog = usePostHog();

  return useCallback<SetPersonPropertyFn>(
    (properties) => setPersonProperty(posthog, properties),
    [posthog],
  );
}
